import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Clock, Heart, SearchX } from "lucide-react"
import type { ReactNode } from "react"
import { useFavorites } from "@/entities/favorite"
import { PostCard, PostCardSkeleton } from "@/entities/post"
import { useRecentViews } from "@/entities/view-history"
import { useUrlState } from "@/shared/hooks/use-url-state"
import { formatRelativeTime } from "@/shared/lib/format-relative-time"
import { Button } from "@/shared/ui/button"
import { Crossfade } from "@/shared/ui/crossfade"
import { EmptyState } from "@/shared/ui/empty-state"
import { HapticLink } from "@/shared/ui/haptic-link"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageTransition } from "@/shared/ui/page-transition"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs"
import { PostCardActions } from "@/widgets/posts-feed"

const TAB_LIST = "grid w-full grid-cols-2 gap-1 rounded-full bg-white/5 p-1 ring-1 ring-white/10"
const TAB =
	"flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors data-[state=active]:bg-white/10 data-[state=active]:text-primary"

interface SectionProps {
	isLoading: boolean
	isError: boolean
	error: unknown
	onRetry: () => void
	count: number
	errorMessage: string
	emptyTitle: string
	emptyHint: string
	children: ReactNode
}

function LibrarySection({
	isLoading,
	isError,
	error,
	onRetry,
	count,
	errorMessage,
	emptyTitle,
	emptyHint,
	children,
}: SectionProps) {
	const state = isLoading ? "loading" : isError ? "error" : count === 0 ? "empty" : "content"

	return (
		<Crossfade state={state}>
			{state === "loading" && (
				<div className="grid grid-cols-1 gap-4 lg:gap-6">
					{Array.from({ length: 4 }).map((_, index) => (
						<PostCardSkeleton key={index} />
					))}
				</div>
			)}

			{state === "error" && (
				<LoadErrorState message={extractApiErrorMessage(error, errorMessage)} onRetry={onRetry} />
			)}

			{state === "empty" && (
				<EmptyState
					icon={SearchX}
					title={emptyTitle}
					action={
						<Button asChild>
							<HapticLink to="/">Найти материалы</HapticLink>
						</Button>
					}
				>
					{emptyHint}
				</EmptyState>
			)}

			{state === "content" && <div className="grid grid-cols-1 gap-4 lg:gap-6">{children}</div>}
		</Crossfade>
	)
}

export function LibraryPage() {
	const [activeTab, setActiveTab] = useUrlState("tab", "recent")
	const {
		data: recentViews = [],
		isLoading: recentLoading,
		isError: recentError,
		error: recentQueryError,
		refetch: refetchRecent,
	} = useRecentViews()
	const {
		data: favorites = [],
		isLoading: favoritesLoading,
		isError: favoritesError,
		error: favoritesQueryError,
		refetch: refetchFavorites,
	} = useFavorites()

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_24%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto max-w-3xl px-3 pt-5 pb-6 sm:px-4">
					<h1 className="text-3xl font-bold tracking-tight">Библиотека</h1>
					<p className="mt-2 text-sm leading-snug text-muted-foreground">
						Что вы смотрели и что отложили на потом.
					</p>
				</div>
			</header>

			<div className="container mx-auto max-w-3xl px-3 pb-6 sm:px-4 lg:pb-8">
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList className={TAB_LIST}>
						<TabsTrigger value="recent" className={TAB}>
							<Clock className="size-4" />
							Недавние
							{recentViews.length > 0 && (
								<span className="text-xs text-muted-foreground">{recentViews.length}</span>
							)}
						</TabsTrigger>
						<TabsTrigger value="favorites" className={TAB}>
							<Heart className="size-4" />
							Избранное
							{favorites.length > 0 && (
								<span className="text-xs text-muted-foreground">{favorites.length}</span>
							)}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="recent" className="mt-6">
						<LibrarySection
							isLoading={recentLoading}
							isError={recentError}
							error={recentQueryError}
							onRetry={() => void refetchRecent()}
							count={recentViews.length}
							errorMessage="Не удалось загрузить историю просмотров."
							emptyTitle="История пуста"
							emptyHint="Материалы, которые вы открывали, появятся здесь"
						>
							{recentViews.map((item) => (
								<PostCard
									key={item.id}
									post={item.post}
									viewedAt={formatRelativeTime(item.lastViewedAt)}
									actions={<PostCardActions postId={item.post.id} />}
								/>
							))}
						</LibrarySection>
					</TabsContent>

					<TabsContent value="favorites" className="mt-6">
						<LibrarySection
							isLoading={favoritesLoading}
							isError={favoritesError}
							error={favoritesQueryError}
							onRetry={() => void refetchFavorites()}
							count={favorites.length}
							errorMessage="Не удалось загрузить избранное."
							emptyTitle="Нет избранного"
							emptyHint="Нажмите на сердце в карточке материала, чтобы отложить его сюда"
						>
							{favorites.map((favorite) => (
								<PostCard
									key={favorite.id}
									post={favorite.post}
									actions={<PostCardActions postId={favorite.post.id} />}
								/>
							))}
						</LibrarySection>
					</TabsContent>
				</Tabs>
			</div>
		</PageTransition>
	)
}
